import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders } from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import Sidebar from '../Sidebar';
import NotificationInbox from '../../components/NotificationInbox';
import ReviewWorkspace from '../review/ReviewWorkspace';
import './DocumentReviewQueue.css';

const QUEUES = {
  application: {
    queuePath: '/api/coach/review-queue',
    commentPath: (reviewId) => `/api/review-requests/${reviewId}/comments`,
    resolvePath: (reviewId, commentId) =>
      `/api/review-requests/${reviewId}/comments/${commentId}/resolve`,
    reopenPath: (reviewId, commentId) =>
      `/api/review-requests/${reviewId}/comments/${commentId}/reopen`,
    reactPath: (reviewId, commentId) =>
      `/api/review-requests/${reviewId}/comments/${commentId}/react`,
    assignPath: (reviewId, commentId) =>
      `/api/review-requests/${reviewId}/comments/${commentId}/assign`,
    acceptPath: (reviewId, commentId) =>
      `/api/review-requests/${reviewId}/comments/${commentId}/accept-suggestion`,
    rejectPath: (reviewId, commentId) =>
      `/api/review-requests/${reviewId}/comments/${commentId}/reject-suggestion`,
    versionsPath: (documentId) => `/api/application-documents/${documentId}/versions`,
    restorePath: (documentId, versionId) =>
      `/api/application-documents/${documentId}/versions/${versionId}/restore`,
  },
  funding: {
    queuePath: '/api/coach/funding-queue',
    commentPath: (reviewId) => `/api/funding/review-requests/${reviewId}/comments`,
    resolvePath: (reviewId, commentId) =>
      `/api/funding/review-requests/${reviewId}/comments/${commentId}/resolve`,
    reopenPath: (reviewId, commentId) =>
      `/api/funding/review-requests/${reviewId}/comments/${commentId}/reopen`,
    reactPath: (reviewId, commentId) =>
      `/api/funding/review-requests/${reviewId}/comments/${commentId}/react`,
    assignPath: (reviewId, commentId) =>
      `/api/funding/review-requests/${reviewId}/comments/${commentId}/assign`,
    acceptPath: (reviewId, commentId) =>
      `/api/funding/review-requests/${reviewId}/comments/${commentId}/accept-suggestion`,
    rejectPath: (reviewId, commentId) =>
      `/api/funding/review-requests/${reviewId}/comments/${commentId}/reject-suggestion`,
    versionsPath: (documentId) => `/api/funding/documents/${documentId}/versions`,
    restorePath: (documentId, versionId) =>
      `/api/funding/documents/${documentId}/versions/${versionId}/restore`,
  },
};

const STATUS_LABELS = {
  requested: 'Requested',
  in_review: 'In review',
  returned: 'Returned',
};

const KIND_LABELS = {
  application: 'Application',
  funding: 'Funding',
};

const DocumentReviewQueue = ({ embedded = false, kind = 'all' }) => {
  const user = getStoredUser();
  const [searchParams] = useSearchParams();
  const reviewKey = (review) =>
    review ? `${review.queueKind}:${review.id}` : null;

  const [reviews, setReviews] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [filter, setFilter] = useState(kind === 'funding' ? 'funding' : 'all');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const queryReviewId = Number(searchParams.get('review_id') || 0);
  const queryCommentId = Number(searchParams.get('comment_id') || 0);
  const queryKind =
    searchParams.get('kind') || (kind === 'funding' ? 'funding' : '');

  const load = async () => {
    const [applicationRes, fundingRes] = await Promise.all([
      fetch(`${API_BASE_URL}${QUEUES.application.queuePath}`, {
        headers: getAuthHeaders(),
      }),
      fetch(`${API_BASE_URL}${QUEUES.funding.queuePath}`, {
        headers: getAuthHeaders(),
      }),
    ]);
    const applicationData = await applicationRes.json().catch(() => ({}));
    const fundingData = await fundingRes.json().catch(() => ({}));
    const statusRank = { requested: 0, in_review: 1, returned: 2 };
    const rows = [
      ...(applicationData.reviews || []).map((review) => ({
        ...review,
        queueKind: 'application',
      })),
      ...(fundingData.reviews || []).map((review) => ({
        ...review,
        queueKind: 'funding',
      })),
    ].sort((a, b) => {
      const rank = (statusRank[a.status] ?? 9) - (statusRank[b.status] ?? 9);
      if (rank !== 0) return rank;
      const aDue = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
      const bDue = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
      return aDue - bDue;
    });
    setReviews(rows);
    setActiveKey((current) => {
      const fromQuery = rows.find((row) => {
        if (row.id !== queryReviewId) {
          return false;
        }
        return !queryKind || row.queueKind === queryKind;
      });
      if (fromQuery) {
        return reviewKey(fromQuery);
      }
      if (current && rows.some((row) => reviewKey(row) === current)) {
        return current;
      }
      return reviewKey(rows[0]);
    });
  };

  useEffect(() => {
    load().catch((err) => setError(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!queryReviewId) {
      return;
    }
    const match = reviews.find((row) => {
      if (row.id !== queryReviewId) {
        return false;
      }
      return !queryKind || row.queueKind === queryKind;
    });
    if (match) {
      setActiveKey(reviewKey(match));
      if (queryKind === 'funding' || queryKind === 'application') {
        setFilter(queryKind);
      }
    }
  }, [queryKind, queryReviewId, reviews]);

  const visible = useMemo(
    () =>
      filter === 'all'
        ? reviews
        : reviews.filter((review) => review.queueKind === filter),
    [filter, reviews]
  );
  const active =
    visible.find((row) => reviewKey(row) === activeKey) || visible[0] || null;
  const documentBody = active?.document?.body || '';
  const config = QUEUES[active?.queueKind] || QUEUES.application;
  const counts = {
    all: reviews.length,
    application: reviews.filter((row) => row.queueKind === 'application').length,
    funding: reviews.filter((row) => row.queueKind === 'funding').length,
  };

  const loadVersions = async (queueKind, documentId) => {
    if (!documentId) {
      setVersions([]);
      return;
    }
    const queue = QUEUES[queueKind] || QUEUES.application;
    setVersionsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}${queue.versionsPath(documentId)}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setVersions(data.versions || []);
    } catch (_err) {
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  useEffect(() => {
    loadVersions(active?.queueKind, active?.document?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.queueKind, active?.document?.id]);

  const postJson = async (path, payload) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: payload ? JSON.stringify(payload) : undefined,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Could not save comment');
      }
      setError('');
      await load();
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const studentName =
    active?.document?.student_name || active?.student?.name || 'Student';
  const heading = active?.document?.school_name
    ? `${active.document.school_name} · ${active.document.program_name || ''}`.trim()
    : active?.document?.title || 'Document';

  const body = (
    <div className="drq">
      <header className="drq-header">
        <div className="drq-header__top">
          <div>
            <p className="drq-kicker">Document reviews</p>
            <h1>Document reviews</h1>
            <p>Application and funding drafts students sent you.</p>
          </div>
          <NotificationInbox variant="sidebar" />
        </div>
        <div className="drq-filters" role="tablist" aria-label="Document type">
          {[
            ['all', 'All'],
            ['application', 'Applications'],
            ['funding', 'Funding'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={filter === value}
              className={`drq-filter${filter === value ? ' is-active' : ''}`}
              onClick={() => setFilter(value)}
            >
              {label}
              <span>{counts[value]}</span>
            </button>
          ))}
        </div>
      </header>

      {!visible.length ? (
        <p className="drq-empty">No documents waiting for your review.</p>
      ) : (
        <div className="drq-layout">
          <aside className="drq-list" aria-label="Submitted documents">
            {visible.map((review) => (
              <button
                type="button"
                key={`${review.queueKind}-${review.id}`}
                className={`drq-item${reviewKey(review) === reviewKey(active) ? ' is-active' : ''}`}
                onClick={() => setActiveKey(reviewKey(review))}
              >
                <strong>{review.document?.title || 'Untitled'}</strong>
                <span className="drq-item__meta">
                  {review.document?.student_name || review.student?.name || 'Student'}
                </span>
                <span className="drq-item__tags">
                  <span className="drq-chip">{KIND_LABELS[review.queueKind]}</span>
                  <span className="drq-chip is-status">
                    {STATUS_LABELS[review.status] || review.status}
                  </span>
                  {review.is_overdue ? (
                    <span className="drq-chip is-overdue">Overdue</span>
                  ) : null}
                </span>
              </button>
            ))}
          </aside>
          {active ? (
            <section className="drq-detail">
              <header className="drq-detail__header">
                <p className="drq-kicker">
                  {KIND_LABELS[active.queueKind]} ·{' '}
                  {STATUS_LABELS[active.status] || active.status}
                </p>
                <h2>{heading}</h2>
                <p>
                  {studentName} asked you to review{' '}
                  <strong>{active.document?.title}</strong>
                </p>
              </header>
              <ReviewWorkspace
                body={documentBody}
                comments={(active.comments || []).map((row) => ({
                  ...row,
                  review_id: active.id,
                }))}
                selectedCommentId={queryCommentId || null}
                canReturn
                saving={saving}
                error={error}
                currentUserId={user?.id}
                collaborators={active.collaborators || []}
                versions={versions}
                versionsLoading={versionsLoading}
                onSubmitComment={(payload) =>
                  postJson(config.commentPath(active.id), {
                    excerpt: payload.excerpt,
                    comment: payload.comment,
                    parent_id: payload.parent_id,
                    start_offset: payload.start_offset,
                    end_offset: payload.end_offset,
                    mark_returned: payload.mark_returned,
                    assigned_user_id: payload.assigned_user_id,
                    suggestion_text: payload.suggestion_text,
                    mentioned_user_ids: payload.mentioned_user_ids,
                  })
                }
                onResolve={(commentId) =>
                  postJson(config.resolvePath(active.id, commentId))
                }
                onReopen={(commentId) =>
                  postJson(config.reopenPath(active.id, commentId))
                }
                onReact={(commentId, emoji) =>
                  postJson(config.reactPath(active.id, commentId), { emoji })
                }
                onAssign={(commentId, assignedUserId) =>
                  postJson(config.assignPath(active.id, commentId), {
                    assigned_user_id: assignedUserId,
                  })
                }
                onAcceptSuggestion={(commentId) =>
                  postJson(config.acceptPath(active.id, commentId))
                }
                onRejectSuggestion={(commentId) =>
                  postJson(config.rejectPath(active.id, commentId))
                }
              />
            </section>
          ) : null}
        </div>
      )}
    </div>
  );

  if (embedded) {
    return body;
  }
  return (
    <div className="layout">
      <Sidebar />
      <main className="drq-page">{body}</main>
    </div>
  );
};

export default DocumentReviewQueue;
